import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation as useConvexMutation } from "convex/react";
import * as Sentry from "@sentry/react";
import Icon from "./Icon";
import { GlassCard, Btn } from "./UI";
import { useDialogA11y } from "@/lib/useDialogA11y";
import { api } from "../../../../convex/_generated/api";

/**
 * AddToMealPlanSheet
 *
 * Bottom-sheet modal that lets a sister plan a recipe for a specific
 * day + meal slot. Opens from RecipeDetail's "Add to meal plan" button.
 *
 * Day picker: the next 7 days starting today (Today, Tomorrow, Wed, Thu,...).
 * Meal picker: morning / midday / snack / evening — same vocabulary as
 *   nutritionLogs and Garden.
 * Servings: defaults to recipe.servings, clamped to [1, 20].
 *
 * On confirm: api.recipesDb.addToMealPlan (idempotent on user+date+meal+
 * recipe — re-tap just updates servings on the existing row).
 */

type Meal = "morning" | "midday" | "snack" | "evening";

const MEALS: Array<{ value: Meal; label: string; emoji: string }> = [
  { value: "morning", label: "Morning", emoji: "🌅" },
  { value: "midday", label: "Midday", emoji: "🥗" },
  { value: "snack", label: "Snack", emoji: "🌿" },
  { value: "evening", label: "Evening", emoji: "🌙" },
];

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Build the next 7 days starting today, with friendly labels. */
function nextSevenDays(): Array<{ dateKey: string; label: string; sub: string }> {
  const out: Array<{ dateKey: string; label: string; sub: string }> = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const dateKey = `${yyyy}-${mm}-${dd}`;
    const label = i === 0 ? "Today" : i === 1 ? "Tomorrow" : DAY_LABELS[d.getDay()];
    const sub = `${d.toLocaleString("en-US", { month: "short" })} ${d.getDate()}`;
    out.push({ dateKey, label, sub });
  }
  return out;
}

export default function AddToMealPlanSheet({
  open,
  onClose,
  userId,
  recipeId,
  recipeServings,
  recipeTitle,
}: {
  open: boolean;
  onClose: () => void;
  userId: string;
  recipeId: string;
  recipeServings: number;
  recipeTitle: string;
}) {
  // Recompute the day list whenever the sheet opens. Without this, a sheet
  // left open across midnight would show "Today" pointing at yesterday.
  const days = useMemo(() => nextSevenDays(), [open]);

  // NaN-safe initial servings derivation (recipe.servings of 0 / undefined / NaN).
  const initialServings = Number.isFinite(recipeServings) && recipeServings > 0
    ? Math.max(1, Math.min(20, Math.round(recipeServings)))
    : 4;

  const [dateKey, setDateKey] = useState(days[0].dateKey);
  const [meal, setMeal] = useState<Meal>("evening");
  const [servings, setServings] = useState<number>(initialServings);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  // Rendered in the sheet when the write fails. The sheet stays open and
  // "done" stays false, so nothing here can read as a save that worked.
  const [error, setError] = useState<string>("");

  const addToMealPlan = useConvexMutation(api.recipesDb.addToMealPlan);

  // Reset selection state when sheet reopens (handles the case where the
  // sheet remounts on a different recipe via React's conditional render).
  useEffect(() => {
    if (open) {
      setDateKey(days[0].dateKey);
      setMeal("evening");
      setServings(initialServings);
      setSaving(false);
      setDone(false);
      setError("");
    }
  }, [open, recipeId, initialServings, days]);

  // Track unmount + clear the "Planted!" flash timer if user closes mid-flash
  // so we don't hit setState-on-unmounted warnings.
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, []);

  const adjustServings = (delta: number) => {
    setServings((s) => Math.max(1, Math.min(20, s + delta)));
  };

  const onConfirm = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await addToMealPlan({ userId, dateKey, meal, recipeId, servings });
      if (!mountedRef.current) return;
      setDone(true);
      flashTimerRef.current = setTimeout(() => {
        flashTimerRef.current = null;
        if (!mountedRef.current) return;
        setDone(false);
        onClose();
      }, 900);
    } catch (err) {
      console.error("[AddToMealPlanSheet] addToMealPlan mutation failed", err);
      Sentry.captureException(err, { tags: { subsystem: "add-to-meal-plan" } });
      if (mountedRef.current) {
        setError(
          "We couldn't add this to your plan just now, so nothing was saved. Check your connection and tap Plant it again.",
        );
      }
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const titleId = "addtomealplan-title";
  const { containerRef } = useDialogA11y({ open, onClose, titleId });

  if (!open) return null;

  const selectedDay = days.find((d) => d.dateKey === dateKey);

  return (
    <div
      className="fixed inset-0 flex items-end justify-center"
      style={{ background: "rgba(22,56,40,0.55)", zIndex: 70 }}
      onClick={onClose}
      data-testid="meal-plan-sheet-overlay"
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--mnf-cream)",
          borderRadius: "24px 24px 0 0",
          padding: 22,
          maxHeight: "88vh",
          overflowY: "auto",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Add to meal plan</p>
            <h2
              id={titleId}
              className="mnf-serif"
              style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.15 }}
            >
              {recipeTitle}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="button-close-meal-plan"
            className="hover-elevate"
            style={{
              width: 36, height: 36, borderRadius: 999,
              background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)",
              border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* Day picker — horizontal scroller of next 7 days */}
        <GlassCard padding={14}>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", marginBottom: 8 }}>When</p>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
            {days.map((d) => {
              const active = d.dateKey === dateKey;
              return (
                <button
                  key={d.dateKey}
                  data-testid={`day-${d.dateKey}`}
                  onClick={() => setDateKey(d.dateKey)}
                  className="hover-elevate shrink-0"
                  style={{
                    minWidth: 72,
                    padding: "10px 12px",
                    borderRadius: 14,
                    background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                    color: active ? "#F5F0E8" : "var(--mnf-fd)",
                    border: "1px solid transparent",
                    cursor: "pointer",
                    textAlign: "center",
                  }}
                >
                  <p className="mnf-body" style={{ fontSize: 13, fontWeight: 700 }}>{d.label}</p>
                  <p className="mnf-body" style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>{d.sub}</p>
                </button>
              );
            })}
          </div>
        </GlassCard>

        {/* Meal picker — 4-up grid */}
        <div className="mt-3">
          <GlassCard padding={14}>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", marginBottom: 8 }}>Which meal</p>
            <div className="grid grid-cols-4 gap-2">
              {MEALS.map((m) => {
                const active = m.value === meal;
                return (
                  <button
                    key={m.value}
                    data-testid={`meal-${m.value}`}
                    onClick={() => setMeal(m.value)}
                    className="hover-elevate"
                    style={{
                      padding: "10px 6px",
                      borderRadius: 12,
                      background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                      color: active ? "#F5F0E8" : "var(--mnf-fd)",
                      border: "1px solid transparent",
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 18, lineHeight: 1 }}>{m.emoji}</div>
                    <p className="mnf-body" style={{ fontSize: 11, fontWeight: 700, marginTop: 4 }}>
                      {m.label}
                    </p>
                  </button>
                );
              })}
            </div>
          </GlassCard>
        </div>

        {/* Servings */}
        <div className="mt-3">
          <GlassCard padding={14}>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", marginBottom: 8 }}>Servings</p>
            <div className="flex items-center justify-between">
              <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
                {servings === recipeServings
                  ? `Recipe makes ${recipeServings}`
                  : `${servings} of ${recipeServings} (recipe yield)`}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => adjustServings(-1)}
                  data-testid="button-servings-down"
                  aria-label="Decrease servings"
                  className="hover-elevate"
                  style={{
                    width: 34, height: 34, borderRadius: 10,
                    background: "rgba(30,74,53,0.08)", color: "var(--mnf-fd)",
                    border: "none", cursor: "pointer", fontSize: 18,
                  }}
                >−</button>
                <span
                  className="mnf-body text-center"
                  style={{ minWidth: 32, fontSize: 16, fontWeight: 600, color: "var(--mnf-fd)" }}
                >
                  {servings}
                </span>
                <button
                  onClick={() => adjustServings(1)}
                  data-testid="button-servings-up"
                  aria-label="Increase servings"
                  className="hover-elevate"
                  style={{
                    width: 34, height: 34, borderRadius: 10,
                    background: "rgba(30,74,53,0.08)", color: "var(--mnf-fd)",
                    border: "none", cursor: "pointer", fontSize: 18,
                  }}
                >+</button>
              </div>
            </div>
          </GlassCard>
        </div>

        {error && (
          <p
            role="alert"
            data-testid="meal-plan-error"
            className="mnf-body"
            style={{ fontSize: 12, color: "var(--mnf-t)", lineHeight: 1.5, marginTop: 14 }}
          >
            {error}
          </p>
        )}

        <div className="mt-4">
          <Btn
            data-testid="button-confirm-meal-plan"
            variant="primary"
            block
            size="lg"
            onClick={onConfirm}
            disabled={saving || done}
          >
            {done ? (
              <>
                <Icon name="check_circle" size={18} /> Planted on {selectedDay?.label.toLowerCase()}
              </>
            ) : (
              <>
                <Icon name="event_available" size={18} /> {saving ? "Planting…" : "Plant it"}
              </>
            )}
          </Btn>
        </div>
      </div>
    </div>
  );
}
